import { PREBID_CONFIG } from "../config";
import { buildPrebidAdUnits } from "./prebid-config";

const googletag = window.googletag || (window.googletag = { cmd: [] });

let prebidLoaded = false;
let prebidLoadPromise = null;

// Load the Prebid.js script and configure global settings.
// Returns a Promise that resolves to true on success, false on failure.
// On failure, ads will fall back to direct GPT refresh.
export function loadPrebidScript() {
  if (prebidLoadPromise) return prebidLoadPromise;

  prebidLoadPromise = new Promise((resolve) => {
    // Initialize the pbjs queue regardless — the script may already be loaded
    window.pbjs = window.pbjs || { que: [] };

    const script = document.createElement("script");
    script.src = PREBID_CONFIG.SCRIPT_URL;
    script.async = true;

    script.onload = () => {
      console.log("Prebid.js loaded");
      window.pbjs.que.push(() => {
        window.pbjs.setConfig({
          debug: PREBID_CONFIG.DEBUG,
          bidderTimeout: PREBID_CONFIG.TIMEOUT,
          enableSendAllBids: true,
          priceGranularity: PREBID_CONFIG.PRICE_GRANULARITY,
          userSync: {
            iframeEnabled: true,
            syncsPerBidder: 3,
            syncDelay: 3000,
            filterSettings: {
              all: {
                bidders: '*',
                filter: 'include'
              }
            }
          }
        });
      });
      prebidLoaded = true;
      resolve(true);
    };

    script.onerror = () => {
      console.warn("Prebid.js failed to load — falling back to direct GPT refresh");
      prebidLoaded = false;
      resolve(false);
    };

    document.head.appendChild(script);
  });

  return prebidLoadPromise;
}

// Run a Prebid auction for the given display slot names, then refresh GPT slots.
// If Prebid is unavailable, falls through immediately to googletag.pubads().refresh().
export function requestBidsAndRefresh(slotNames, gptSlots, adUnitCodePrefix, sessionData) {
  // If Prebid didn't load or pbjs.requestBids is unavailable, go direct
  if (!prebidLoaded || !window.pbjs || typeof window.pbjs.requestBids !== 'function') {
    googletag.cmd.push(() => {
      googletag.pubads().refresh(gptSlots);
    });
    return;
  }

  const pbjs = window.pbjs;
  const adUnits = buildPrebidAdUnits(slotNames, adUnitCodePrefix);

  pbjs.que.push(() => {
    // Remove any previously registered ad units for these slots to avoid duplicates
    const existingCodes = (pbjs.adUnits || []).map((u) => u.code);
    const codesToRemove = adUnits.map((u) => u.code).filter((c) => existingCodes.includes(c));
    if (codesToRemove.length > 0) {
      pbjs.removeAdUnit(codesToRemove);
    }

    pbjs.addAdUnits(adUnits);

    pbjs.requestBids({
      bidsBackHandler: (bidResponses) => {
        // Log auction results to console (backend doesn't support prebid events yet)
        const hasBids = Object.keys(bidResponses).some(
          (code) => bidResponses[code] && bidResponses[code].bids && bidResponses[code].bids.length > 0
        );
        console.log("Prebid auction complete", { slots: slotNames, hasBids });

        // Set targeting on GPT slots (no-op if no bids came back)
        pbjs.setTargetingForGPTAsync();

        // Refresh GPT — GAM will evaluate any Prebid bids vs direct demand
        googletag.cmd.push(() => {
          googletag.pubads().refresh(gptSlots);
        });
      },
      timeout: PREBID_CONFIG.TIMEOUT
    });
  });
}
